import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Heart, Loader2, Pause, Play, Repeat, Repeat1, Shuffle, Sparkles, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { logError } from "@/lib/log";
import { SleepMix, useSleepMix } from "@/components/bedtime/SleepMix";
import { playStory, primeStoryAudio, storyAudio } from "@/lib/storyAudio";

export const THEMES = [["forest", "🌲", "Forest"], ["ocean", "🌊", "Ocean"], ["rain", "🌧️", "Rain"], ["stars", "✨", "Stars"], ["cabin", "🛖", "Cosy cabin"]];
const themeIcon = (t) => THEMES.find((x) => x[0] === t)?.[1] || "🌙";
const REPEAT_KEY = "mm_story_repeat";
const REPEATS = [["off", Repeat, "Repeat off"], ["one", Repeat1, "Repeat this story"], ["favorites", Repeat, "Loop my favorites"]];

const IDLE = { id: null, loading: false, playing: false, blocked: false };

const usePlayer = (onEnded) => {
  const audio = useRef(storyAudio());
  const url = useRef(null);
  const [state, setState] = useState(IDLE);
  useEffect(() => {
    const a = audio.current;
    return () => { a.pause(); a.onended = null; if (url.current) URL.revokeObjectURL(url.current); };
  }, []);
  const start = async (story) => {
    try { await playStory(audio.current); setState({ ...IDLE, id: story.id, playing: true }); }
    catch (e) {
      if (e?.name === "NotAllowedError") { setState({ ...IDLE, id: story.id, blocked: true }); return; }
      logError("Story play failed:", e); toast.error("Couldn't play this story. Tap play to try again"); setState(IDLE);
    }
  };
  const load = async (story) => {
    const a = audio.current;
    a.pause();
    setState({ ...IDLE, id: story.id, loading: true });
    try {
      const { data } = await api.get(`/bedtime/stories/${story.id}/audio`, { responseType: "blob" });
      if (url.current) URL.revokeObjectURL(url.current);
      url.current = URL.createObjectURL(data);
      a.src = url.current;
      a.onended = () => { setState((s) => ({ ...s, playing: false })); onEnded.current?.(story, a); };
      if ("mediaSession" in navigator) navigator.mediaSession.metadata = new window.MediaMetadata({ title: story.title, artist: "Mindful Me sleep story" });
      await start(story);
    } catch (e) { logError("Story play failed:", e); toast.error("Couldn't play this story. Tap play to try again"); setState(IDLE); }
  };
  // Tap handlers: unlock the shared player synchronously, before any await
  const play = (story) => {
    const a = audio.current;
    if (state.id === story.id && state.blocked) return start(story);
    primeStoryAudio();
    if (state.id !== story.id || state.loading) return load(story);
    if (a.paused) return start(story);
    a.pause(); setState((s) => ({ ...s, playing: false }));
    return undefined;
  };
  const tapLoad = (story) => { primeStoryAudio(); return load(story); };
  const replay = (a) => { a.currentTime = 0; return start({ id: state.id }); };
  return { state, play, load, tapLoad, replay, audio };
};

const TapToPlay = ({ story, player }) => (
  <button onClick={() => player.play(story)} className="w-full rounded-2xl bg-white/10 ring-2 ring-[#F28FAD] text-[#F4E9D8] font-semibold py-4 mt-4 flex items-center justify-center gap-2 animate-pulse" data-testid="bedtime-tap-to-play">
    <Play size={20} />Tap to play "{story.title}"
  </button>
);

const StoryRow = ({ s, player, onFav, onDelete }) => {
  const on = player.state.id === s.id;
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-white/5 p-3" data-testid={`bedtime-story-${s.id}`}>
      <button onClick={() => player.play(s)} className="w-12 h-12 shrink-0 rounded-full bg-[#F3C98B] text-[#141A33] flex items-center justify-center" aria-label={on && player.state.playing ? "Pause" : "Play"} data-testid={`bedtime-story-play-${s.id}`}>
        {on && player.state.loading ? <Loader2 size={18} className="animate-spin" /> : on && player.state.playing ? <Pause size={18} /> : <Play size={18} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{themeIcon(s.theme)} {s.title}</div>
        <div className="text-xs opacity-60">{new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
      </div>
      <button onClick={() => onFav(s)} className="p-2" aria-label={s.favorite ? "Remove favourite" : "Favourite"} data-testid={`bedtime-story-fav-${s.id}`}><Heart size={18} className={s.favorite ? "fill-[#F28FAD] text-[#F28FAD]" : "opacity-60"} /></button>
      <button onClick={() => onDelete(s)} className="p-2 opacity-50 hover:opacity-90" aria-label="Delete story" data-testid={`bedtime-story-delete-${s.id}`}><Trash2 size={16} /></button>
    </div>
  );
};

const Tab = ({ on, onClick, children, testId }) => (
  <button onClick={onClick} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${on ? "bg-white/15" : "opacity-60 hover:opacity-100"}`} aria-pressed={on} data-testid={testId}>{children}</button>
);

const LibraryBar = ({ tab, setTab, favCount, repeat, cycleRepeat, onSurprise }) => {
  const [, RIcon, rLabel] = REPEATS.find((r) => r[0] === repeat);
  return (
    <div className="flex items-center gap-1 mt-6 mb-2 flex-wrap">
      <Tab on={tab === "all"} onClick={() => setTab("all")} testId="bedtime-tab-all">All stories</Tab>
      <Tab on={tab === "favorites"} onClick={() => setTab("favorites")} testId="bedtime-tab-favorites">Favorites ({favCount})</Tab>
      <div className="flex-1" />
      <button onClick={onSurprise} disabled={!favCount} className="p-2 rounded-full hover:bg-white/10 disabled:opacity-30" aria-label="Surprise me with a favorite" title="Surprise me" data-testid="bedtime-surprise"><Shuffle size={16} /></button>
      <button onClick={cycleRepeat} className={`p-2 rounded-full hover:bg-white/10 flex items-center gap-1 text-xs ${repeat === "off" ? "opacity-60" : "text-[#F3C98B]"}`} aria-label={rLabel} title={rLabel} data-testid="bedtime-repeat" data-mode={repeat}>
        <RIcon size={16} />{repeat === "favorites" && <span>Favorites</span>}
      </button>
    </div>
  );
};

const pickRandom = (list, notId) => {
  const pool = list.length > 1 ? list.filter((s) => s.id !== notId) : list;
  return pool[Math.floor(Math.random() * pool.length)];
};

// Pick a theme for a new AI story (read aloud), or replay a saved one
export const StoryStep = ({ onPlayed, storiesLeft, autoFavorite }) => {
  const [stories, setStories] = useState(null);
  const [theme, setTheme] = useState("forest");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState(autoFavorite ? "favorites" : "all");
  const [repeat, setRepeat] = useState(() => localStorage.getItem(REPEAT_KEY) || "off");
  const ended = useRef(null);
  const player = usePlayer(ended);
  const mix = useSleepMix(player.audio);
  const favs = (stories || []).filter((s) => s.favorite);
  ended.current = (story, a) => {
    if (repeat === "one") return player.replay(a);
    if (repeat === "favorites" && favs.length) {
      const i = favs.findIndex((s) => s.id === story.id);
      return player.load(favs[(i + 1) % favs.length]);
    }
    return mix.storyEnded();
  };
  const autoRef = useRef(autoFavorite);
  const loadRef = useRef(player.load);
  loadRef.current = player.load;
  useEffect(() => {
    api.get("/bedtime/stories").then((r) => {
      setStories(r.data);
      const first = r.data.find((s) => s.favorite);
      if (autoRef.current && first) loadRef.current(first);
      autoRef.current = false;
    }).catch(() => setStories([]));
  }, []);
  useEffect(() => { if (player.state.playing) onPlayed(player.state.id); }, [player.state.playing, player.state.id, onPlayed]);
  const [left, setLeft] = useState(storiesLeft ?? 3);
  useEffect(() => { if (storiesLeft != null) setLeft(storiesLeft); }, [storiesLeft]);
  const make = async () => {
    primeStoryAudio();
    setBusy(true);
    try { const { data } = await api.post("/bedtime/stories", { theme }); setLeft((n) => Math.max(0, n - 1)); setStories((l) => [data, ...(l || [])]); setTab("all"); player.load(data); }
    catch (e) { toast.error(errMsg(e, "Tonight's story couldn't be made right now")); } finally { setBusy(false); }
  };
  const fav = async (s) => { const { data } = await api.post(`/bedtime/stories/${s.id}/favorite`); setStories((l) => l.map((x) => (x.id === s.id ? { ...x, favorite: data.favorite } : x))); };
  const del = async (s) => { await api.delete(`/bedtime/stories/${s.id}`); setStories((l) => l.filter((x) => x.id !== s.id)); };
  const cycleRepeat = () => {
    const next = REPEATS[(REPEATS.findIndex((r) => r[0] === repeat) + 1) % REPEATS.length][0];
    setRepeat(next); localStorage.setItem(REPEAT_KEY, next);
  };
  const surprise = () => { const s = pickRandom(favs, player.state.id); if (s) { setTab("favorites"); player.tapLoad(s); } };
  const shown = tab === "favorites" ? favs : stories || [];
  return (
    <div className="w-full max-w-md" data-testid="bedtime-story-step">
      <h2 className="text-2xl font-semibold mb-1">A story for sleep</h2>
      <p className="opacity-70 text-sm mb-5">Choose a gentle place. Mia writes a new story and reads it softly. It keeps playing with your screen off.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {THEMES.map(([id, icon, name]) => (
          <button key={id} onClick={() => setTheme(id)} className={`rounded-full px-4 py-2 text-sm border transition-colors ${theme === id ? "bg-[#F3C98B] text-[#141A33] border-[#F3C98B]" : "border-white/20 hover:bg-white/10"}`} data-testid={`bedtime-theme-${id}`}>{icon} {name}</button>
        ))}
      </div>
      <button onClick={make} disabled={busy || !left} className="w-full rounded-full bg-[#F3C98B] text-[#141A33] font-semibold py-3.5 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="bedtime-story-new">
        {busy ? <><Loader2 size={18} className="animate-spin" />Writing your story… (about 20 seconds)</> : <><Sparkles size={18} />New story ({left} left today)</>}
      </button>
      {!left && <a href="/more-ai" className="block text-center text-sm underline opacity-80 mt-2" data-testid="bedtime-more-stories">Want more stories today? Add an extra allowance</a>}
      {player.state.blocked && stories?.find((s) => s.id === player.state.id) && <TapToPlay story={stories.find((s) => s.id === player.state.id)} player={player} />}
      <SleepMix mix={mix} />
      {stories?.length > 0 && <>
        <LibraryBar tab={tab} setTab={setTab} favCount={favs.length} repeat={repeat} cycleRepeat={cycleRepeat} onSurprise={surprise} />
        {shown.length ? (
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">{shown.map((s) => <StoryRow key={s.id} s={s} player={player} onFav={fav} onDelete={del} />)}</div>
        ) : <p className="text-sm opacity-60 py-4 text-center" data-testid="bedtime-favorites-empty">Tap the heart on a story you love to keep it here.</p>}
      </>}
    </div>
  );
};
